import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { changeProps } from "find-and";
import { GeometryType, ProjectContentItemDc } from "@evergis/api";
import { IOption, RangeValue } from "@evergis/ui";

import { resolveSettledResourceData } from "../../utils/promiseAllSettled";
import { isFailedService, PostgresLayerService } from "../../interfaces/service";
import { getLayerInfo } from "../../evergis/core/layer/getLayerInfo";
import { api } from "../../evergis/api";
import { getProjectItems, getProjectItemsLength, getProjectLayers } from "../../ducks/project/selectors";
import { getLayerInfosError, getLayerInfosLoading } from "../../ducks/layers/selectors";
import { setLayerInfosError, setLayerInfosLoading } from "../../ducks/layers/layers";
import { getTempSourceLayers } from "../../ducks/tempSourceLayers/selectors";
import { useProjectUpdate } from "../../ducks/project/hooks";
import { usePostgresLayer } from "../layer/usePostgresLayer";
import { useDefaultScaleRange } from "../style/useDefaultScaleRange";
import { Layers, setLayersMeta } from "../../ducks/project/project";
import { LayerInfo } from "../../interfaces/resourceInfo";
import { ExtendedProjectContentItemDc } from "../../interfaces/features";
import { LayerConfigurationDc } from "../../modules/Configuration/types";

export function useProjectLayersLength(): number {
  return useSelector(getProjectItemsLength);
}

export function useProjectLayers(): ProjectContentItemDc[] {
  return useSelector(getProjectItems);
}

export const combineLayers = (
  layers?: ExtendedProjectContentItemDc[],
  options?: { onlyVisible?: boolean; savePath?: boolean; path?: string[] },
) => {
  const { onlyVisible, savePath, path } = options || {};

  return layers?.reduce((result, item) => {
    if (onlyVisible && !item.isVisible) {
      return result;
    }

    if (item.children) {
      if (savePath) {
        options.path = [...(path || []), item.alias];
      }

      if (!onlyVisible || item.isVisible) {
        result.push(...combineLayers(item.children, options));
      }
    } else {
      const newItem = { ...item };

      if (savePath) {
        newItem.path = path;
      }

      result.push(newItem);
    }

    return result;
  }, []);
};

export const useCombinedProjectLayers = (onlyVisible = true) => {
  const projectLayers = useProjectLayers();

  return useMemo(() => combineLayers(projectLayers, { onlyVisible }), [onlyVisible, projectLayers]);
};

export const useProjectLayerNames = (onlyVisible = true): string[] => {
  const combinedLayers = useCombinedProjectLayers(onlyVisible);

  return useMemo(() => combinedLayers.map(({ name }) => name), [combinedLayers]);
};

const resolveLayers = (layers?: Layers, error?: Error, defaultScaleRange?: RangeValue): LayerInfo[] => {
  const services =
    resolveSettledResourceData(
      Object.keys(layers).reduce((result, current) => [...result, layers[current]], []),
      error,
    ) || [];

  return services.reduce(
    (resolvedLayers: LayerInfo[], service: PostgresLayerService) =>
      isFailedService(service) ? resolvedLayers : [...resolvedLayers, getLayerInfo(service, defaultScaleRange)],
    [],
  );
};

const useUpdateProjectItems = () => {
  const layers = useProjectLayers();
  const mutation = useProjectUpdate();
  const savedLayerInfos = useSelector(getProjectLayers);

  useEffect(() => {
    if (!layers?.length || !savedLayerInfos?.length) {
      return;
    }

    const emptyAliasItems = combineLayers(layers, { onlyVisible: false }).filter(
      ({ name, alias }) => !alias && savedLayerInfos[name],
    );

    if (!emptyAliasItems.length) {
      return;
    }

    let items = JSON.parse(JSON.stringify(layers));

    emptyAliasItems.forEach(item => {
      const { alias } = savedLayerInfos[item.name] || {};

      if (alias) {
        items = changeProps(items, { name: item.name }, { alias });
      }
    });

    mutation({ content: { items } });
  }, [layers, savedLayerInfos]);
};

export function useUpdateProjectLayerInfo() {
  const dispatch = useDispatch();
  const savedLayerInfos = useSelector(getProjectLayers);

  return useCallback(
    (configuration: LayerConfigurationDc) => {
      const newLayerInfo = JSON.parse(JSON.stringify(savedLayerInfos));

      newLayerInfo[configuration.name] = {
        ...newLayerInfo[configuration.name],
        configuration: { ...(newLayerInfo[configuration.name]?.configuration || {}), ...configuration },
      };

      dispatch(setLayersMeta(newLayerInfo));
    },
    [dispatch, savedLayerInfos],
  );
}

export function useFetchProjectLayerInfos() {
  const dispatch = useDispatch();
  const loading = useSelector(getLayerInfosLoading);
  const layerNames = useProjectLayerNames(false);
  const defaultScaleRange = useDefaultScaleRange();
  const layerNamesRef = useRef<string[]>([]);

  useUpdateProjectItems();

  useEffect(() => {
    if (
      loading ||
      !layerNames?.length ||
      (layerNamesRef.current?.length && layerNames.every(name => layerNamesRef.current.includes(name)))
    )
      return;

    layerNamesRef.current = layerNames;

    dispatch(setLayerInfosLoading(true));

    api.layers
      .findMany(layerNames)
      .then(response => {
        dispatch(
          setLayersMeta(
            response.reduce(
              (result, current) => ({ ...result, [current.name]: getLayerInfo(current, defaultScaleRange) }),
              {} as Layers,
            ),
          ),
        );
      })
      .catch(error => {
        dispatch(setLayerInfosError(error));
      })
      .finally(() => {
        dispatch(setLayerInfosLoading(false));
      });
  }, [layerNames, loading]);
}

export function useProjectLayerInfos() {
  const savedLayerInfos = useSelector(getProjectLayers);
  const loading = useSelector(getLayerInfosLoading);
  const error = useSelector(getLayerInfosError);
  const defaultScaleRange = useDefaultScaleRange();

  return useMemo(
    () => ({
      layerInfos: resolveLayers(savedLayerInfos, error, defaultScaleRange),
      loading,
      error,
    }),
    [savedLayerInfos, error, defaultScaleRange, loading],
  );
}

export type LayersDropdownListConfig = {
  geometryType?: GeometryType;
  serviceTypes?: string[];
  layerName?: string;
};

export function useLayersDropdownList({ geometryType, serviceTypes, layerName }: LayersDropdownListConfig = {}) {
  const { t } = useTranslation("common");
  const { layerInfos: layers, loading } = useProjectLayerInfos();
  const { postgresLayer } = usePostgresLayer(layerName);

  return [
    useMemo(
      (): IOption[] => [
        { value: null, text: t("notChosen") },
        ...[...layers, postgresLayer]
          .filter(({ geometryType: layerGeometryType }) => (geometryType ? layerGeometryType === geometryType : true))
          .filter(({ type: layerType }) => (serviceTypes ? serviceTypes.includes(layerType) : true))
          .map(layer => ({ text: layer.alias || layer.name, value: layer.name })),
      ],
      [layers, t, geometryType, serviceTypes, postgresLayer],
    ),
    loading,
  ] as const;
}

export function useProjectLayersToRender(onlyVisible = true): ProjectContentItemDc[] {
  const layers = useProjectLayers();
  const tempSourceLayers = useSelector(getTempSourceLayers);
  const allLayers = useMemo(
    (): ProjectContentItemDc[] => [
      ...tempSourceLayers.map(({ name }) => ({ name, isVisible: true, opacity: 1 })),
      ...layers,
    ],
    [layers, tempSourceLayers],
  );

  return useMemo(() => combineLayers(allLayers, { onlyVisible }), [allLayers, onlyVisible]);
}

export const useProjectLayersUpdate = (): [ProjectContentItemDc[], (newItems: ProjectContentItemDc[]) => void] => {
  const updateProject = useProjectUpdate();
  const items = useProjectLayers();

  const updateContentItems = useCallback(
    (newItems: ProjectContentItemDc[]) => {
      updateProject({
        content: {
          items: newItems,
        },
      });
    },
    [updateProject],
  );

  return [items, updateContentItems];
};
